<template>
  <main class="training-detail-page">
    <div class="training-shell">
      <template v-if="session">
        <MarketingToolHeader
          class="training-header"
          eyebrow="Akademie Schulung"
          :title="session.title"
          :description="session.description || 'Alle Details, Voraussetzungen und deine Anmeldung auf einen Blick.'"
          icon="mdi-calendar-star-outline"
          compact-on-mobile>
          <template v-slot:actions>
            <v-btn
              icon
              class="icon-action academy-local-back"
              aria-label="Zurück zum Schulungsplan"
              title="Zurück zum Schulungsplan"
              @click="$router.push('/akademie/schulungen')">
              <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
            <span class="phase-badge" :class="phaseClass(getPhase(session))">{{ getPhase(session) }}</span>
            <span v-if="session.isRefresher" class="refresh-badge"><v-icon>mdi-refresh</v-icon>Auffrischung</span>
          </template>
        </MarketingToolHeader>

        <div
          v-if="session.thumbnailUrl"
          class="training-image"
          :style="{ backgroundImage: `url(${session.thumbnailUrl})` }"
          role="img"
          :aria-label="session.title">
          <div class="training-image-overlay"></div>
        </div>

        <div class="detail-grid">
          <div class="detail-content">
            <section v-if="session.trainingType" class="training-type-banner" aria-label="Schulungstyp">
              <span><v-icon>mdi-certificate-outline</v-icon></span>
              <div>
                <small>{{ session.trainingType.tier }}-Schulung</small>
                <strong>{{ session.trainingType.title }}</strong>
                <p v-if="session.trainingType.description">{{ session.trainingType.description }}</p>
                <p v-else-if="session.trainingType.requiredForLicense">Diese Schulung zählt als Präsenzanforderung für deine Lizenz.</p>
              </div>
            </section>

            <section class="info-grid" aria-label="Schulungsdetails">
              <v-card dark flat class="info-card">
                <span class="info-icon"><v-icon>mdi-calendar-clock-outline</v-icon></span>
                <div>
                  <small>Datum &amp; Zeit</small>
                  <strong>{{ formatDate(session.startsAt) }}</strong>
                  <span>{{ formatTime(session.startsAt) }} – {{ formatTime(session.endsAt) }}</span>
                </div>
              </v-card>
              <v-card dark flat class="info-card">
                <span class="info-icon icon-blue"><v-icon>mdi-map-marker-outline</v-icon></span>
                <div>
                  <small>Ort</small>
                  <strong>{{ session.location || "Ort noch offen" }}</strong>
                  <span>Details zur Durchführung</span>
                </div>
              </v-card>
            </section>

            <v-card dark flat class="content-surface requirements-surface">
              <div class="surface-header">
                <span class="surface-icon"><v-icon>mdi-clipboard-check-outline</v-icon></span>
                <div>
                  <div class="surface-eyebrow">Vor der Anmeldung</div>
                  <h2>Voraussetzungen</h2>
                  <p>Diese Lernmodule müssen vor der Schulung abgeschlossen sein.</p>
                </div>
              </div>

              <div v-if="missingModules.length" class="requirement-state has-missing">
                <span><v-icon>mdi-alert-circle-outline</v-icon></span>
                <div>
                  <strong>Noch nicht alle Voraussetzungen erfüllt</strong>
                  <ul>
                    <li v-for="moduleItem in missingModules" :key="moduleItem._id">{{ moduleItem.title }}</li>
                  </ul>
                </div>
              </div>
              <div v-else class="requirement-state is-ready">
                <span><v-icon>mdi-check</v-icon></span>
                <div>
                  <strong>Alles bereit</strong>
                  <p>Du erfüllst alle Voraussetzungen für diesen Termin.</p>
                </div>
              </div>
            </v-card>

            <v-card v-if="session.hasProtocol" dark flat class="content-surface protocol-surface">
              <div class="surface-header">
                <span class="surface-icon protocol-icon"><v-icon>mdi-file-document-multiple-outline</v-icon></span>
                <div>
                  <div class="surface-eyebrow">Für Teilnehmende</div>
                  <h2>Schulungsprotokoll</h2>
                  <p>Fotos und Dokumente dieser Schulung ansehen oder herunterladen.</p>
                </div>
              </div>

              <div v-if="protocolLoading" class="protocol-loading">
                <v-progress-circular indeterminate color="#58d388" size="28" width="3" />
                <span>Protokoll wird geladen ...</span>
              </div>

              <template v-else-if="protocol">
                <div class="protocol-intro">
                  <span><v-icon>mdi-check-decagram-outline</v-icon></span>
                  <div>
                    <strong>{{ protocol.title || "Schulungsprotokoll" }}</strong>
                    <small>{{ protocol.files.length }} {{ protocol.files.length === 1 ? "Datei" : "Dateien" }} verfügbar</small>
                  </div>
                </div>
                <div v-if="protocol.summary" class="protocol-summary">
                  <v-icon>mdi-text-box-outline</v-icon>
                  <p>{{ protocol.summary }}</p>
                </div>

                <div class="protocol-files-grid">
                  <article v-for="file in protocol.files" :key="file.fileId" class="protocol-file-card">
                    <button
                      v-if="isProtocolImage(file)"
                      type="button"
                      class="protocol-file-preview"
                      :aria-label="`${file.originalName} gross ansehen`"
                      @click="openProtocolImage(file)">
                      <secured-media
                        :src="protocolPreviewPath(file, 'thumbnail')"
                        :cache-key="`participant-protocol-thumbnail-${file.fileId}`"
                        media-type="image" />
                      <span class="preview-hint"><v-icon>mdi-magnify-plus-outline</v-icon>Ansehen</span>
                    </button>
                    <div v-else class="protocol-file-placeholder">
                      <v-icon>{{ isProtocolPdf(file) ? "mdi-file-pdf-box" : "mdi-file-outline" }}</v-icon>
                      <span>{{ isProtocolPdf(file) ? "PDF" : "Datei" }}</span>
                    </div>

                    <div class="protocol-file-copy">
                      <strong :title="file.originalName">{{ file.originalName }}</strong>
                      <span>{{ formatBytes(file.size) }}</span>
                      <p v-if="file.caption">{{ file.caption }}</p>
                    </div>

                    <div class="protocol-file-actions">
                      <v-btn v-if="isProtocolImage(file)" text class="protocol-view-action" @click="openProtocolImage(file)">
                        <v-icon left>mdi-eye-outline</v-icon>
                        Ansehen
                      </v-btn>
                      <v-btn
                        outlined
                        color="#58d388"
                        class="protocol-download-action"
                        :loading="downloadingProtocolFileId === file.fileId"
                        :disabled="Boolean(downloadingProtocolFileId)"
                        @click="downloadProtocolFile(file)">
                        <v-icon left>mdi-download-outline</v-icon>
                        Herunterladen
                      </v-btn>
                    </div>
                  </article>
                </div>
              </template>

              <div v-else class="protocol-error">
                <span><v-icon>mdi-alert-circle-outline</v-icon></span>
                <div>
                  <strong>Protokoll konnte nicht geladen werden</strong>
                  <p>{{ protocolError || "Bitte versuche es erneut." }}</p>
                </div>
                <v-btn outlined color="#58d388" class="protocol-retry-action" @click="loadProtocol">Erneut versuchen</v-btn>
              </div>
            </v-card>
          </div>

          <aside>
            <v-card dark flat class="booking-card">
              <div class="booking-heading">
                <div>
                  <div class="surface-eyebrow">Dein Platz</div>
                  <h2>Anmeldung</h2>
                </div>
                <span class="booking-icon"><v-icon>mdi-ticket-confirmation-outline</v-icon></span>
              </div>

              <div class="seat-overview">
                <div>
                  <small>Verfügbare Plätze</small>
                  <strong
                    >{{ availableSeats }} <span>/ {{ session.maxParticipants }}</span></strong
                  >
                </div>
                <v-progress-circular :value="seatUsage" color="#009640" background-color="rgba(255,255,255,0.08)" size="64" width="6">
                  <v-icon color="#7ee5a6">mdi-account-multiple-outline</v-icon>
                </v-progress-circular>
              </div>

              <div v-if="!hasCatering" class="no-catering-notice">
                <span><v-icon>mdi-cancel</v-icon></span>
                <div>
                  <strong>Keine organisierte Verpflegung</strong>
                  <p>Bitte bring dir für zwischendurch selbst etwas mit.</p>
                </div>
              </div>

              <template v-if="session.myRegistration && session.myRegistration.status !== 'cancelled'">
                <div class="registration-state" :class="registrationStateClass(session.myRegistration.status)">
                  <span><v-icon>{{ registrationStatusIcon(session.myRegistration.status) }}</v-icon></span>
                  <div>
                    <strong>{{ statusLabel(session.myRegistration.status) }}</strong>
                    <p>{{ registrationStatusText }}</p>
                    <p v-if="hasCatering" class="registered-meal">
                      <v-icon>mdi-silverware-fork-knife</v-icon>
                      Essenswunsch: {{ mealPreferenceLabel(session.myRegistration.mealPreference) }}
                    </p>
                  </div>
                </div>
                <v-btn v-if="canCancel" block large outlined color="#ff6b78" class="cancel-action" :loading="cancelling" @click="cancelRegistration">
                  <v-icon left>mdi-close-circle-outline</v-icon>
                  Anmeldung stornieren
                </v-btn>
                <div v-else class="registration-locked-note">
                  <v-icon>mdi-lock-outline</v-icon>
                  <span>{{ registrationLockReason }}</span>
                </div>
              </template>

              <template v-else>
                <v-tooltip bottom :disabled="canRegister">
                  <template v-slot:activator="{ on, attrs }">
                    <div v-bind="attrs" v-on="on">
                      <v-btn
                        block
                        x-large
                        depressed
                        :color="canRegister ? '#009640' : 'rgba(255,255,255,0.08)'"
                        class="register-action"
                        :disabled="!canRegister || registering"
                        :loading="registering"
                        @click="openRegistrationDialog">
                        <v-icon left v-if="canRegister">mdi-check-circle-outline</v-icon>
                        <span>{{ registrationButtonLabel }}</span>
                      </v-btn>
                    </div>
                  </template>
                  <span>{{ registrationBlockReason }}</span>
                </v-tooltip>
              </template>

              <p class="cancellation-note">
                <v-icon>mdi-information-outline</v-icon>
                {{ cancellationNote }}
              </p>
            </v-card>
          </aside>
        </div>
      </template>

      <div v-else class="loading-surface">
        <v-progress-circular v-if="loading" indeterminate color="#58d388" size="48" width="4" />
        <template v-else>
          <span><v-icon>mdi-calendar-remove-outline</v-icon></span>
          <h2>Schulung nicht verfügbar</h2>
          <p>Der Termin konnte nicht geladen werden.</p>
          <v-btn outlined color="#58d388" class="back-action" to="/akademie/schulungen">Zum Schulungsplan</v-btn>
        </template>
      </div>

      <v-dialog v-model="registrationDialog" max-width="34rem" :persistent="registering">
        <v-card dark class="meal-dialog-card">
          <div class="meal-dialog-header">
            <span class="meal-dialog-icon"><v-icon>{{ hasCatering ? "mdi-silverware-fork-knife" : "mdi-cancel" }}</v-icon></span>
            <div>
              <small>Deine Anmeldung</small>
              <h2>{{ hasCatering ? "Essenswunsch" : "Keine organisierte Verpflegung" }}</h2>
            </div>
            <v-btn icon class="meal-dialog-close" :disabled="registering" aria-label="Dialog schliessen" @click="registrationDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>

          <p v-if="hasCatering" class="meal-dialog-intro">Hilf den Organisatoren bei der Planung und wähle kurz aus, was für dich passt.</p>

          <div v-if="hasCatering" class="meal-options" role="radiogroup" aria-label="Essenswunsch auswählen">
            <button
              v-for="option in mealOptions"
              :key="option.value"
              type="button"
              class="meal-option"
              :class="{ 'is-selected': mealPreference === option.value }"
              role="radio"
              :aria-checked="mealPreference === option.value ? 'true' : 'false'"
              @click="mealPreference = option.value">
              <span class="meal-option-icon"
                ><v-icon>{{ option.icon }}</v-icon></span
              >
              <span class="meal-option-copy">
                <strong>{{ option.label }}</strong>
                <small>{{ option.description }}</small>
              </span>
              <v-icon class="meal-option-check">
                {{ mealPreference === option.value ? "mdi-radiobox-marked" : "mdi-radiobox-blank" }}
              </v-icon>
            </button>
          </div>

          <div v-else class="no-catering-dialog-note">
            <v-icon>mdi-information-outline</v-icon>
            <p>Für diese Schulung wird keine organisierte Verpflegung angeboten. Bitte bring dir für zwischendurch selbst etwas mit.</p>
          </div>

          <p v-if="hasCatering" class="meal-privacy-note">
            <v-icon>mdi-shield-lock-outline</v-icon>
            Die Angabe gilt nur für diesen Termin und ist für die Organisatoren sichtbar.
          </p>

          <div class="meal-dialog-actions">
            <v-btn text class="meal-cancel-action" :disabled="registering" @click="registrationDialog = false">Abbrechen</v-btn>
            <v-btn depressed color="#009640" class="meal-confirm-action" :loading="registering" @click="register">
              <v-icon left>mdi-check-circle-outline</v-icon>
              {{ registrationActionLabel }}
            </v-btn>
          </div>
        </v-card>
      </v-dialog>

      <v-dialog v-model="protocolLightboxDialog" max-width="70rem" dark content-class="participant-protocol-lightbox">
        <v-card v-if="protocolLightboxFile" class="protocol-lightbox-card">
          <header class="protocol-lightbox-header">
            <div>
              <strong>{{ protocolLightboxFile.originalName }}</strong>
              <span v-if="protocolLightboxFile.caption">{{ protocolLightboxFile.caption }}</span>
            </div>
            <v-btn icon aria-label="Bildansicht schliessen" @click="protocolLightboxDialog = false"><v-icon>mdi-close</v-icon></v-btn>
          </header>
          <div class="protocol-lightbox-media">
            <secured-media
              :src="protocolPreviewPath(protocolLightboxFile, 'large')"
              :cache-key="`participant-protocol-large-${protocolLightboxFile.fileId}`"
              media-type="image"
              eager />
          </div>
          <footer class="protocol-lightbox-footer">
            <span>{{ formatBytes(protocolLightboxFile.size) }}</span>
            <v-btn
              outlined
              color="#58d388"
              class="protocol-download-action"
              :loading="downloadingProtocolFileId === protocolLightboxFile.fileId"
              :disabled="Boolean(downloadingProtocolFileId)"
              @click="downloadProtocolFile(protocolLightboxFile)">
              <v-icon left>mdi-download-outline</v-icon>
              Original herunterladen
            </v-btn>
          </footer>
        </v-card>
      </v-dialog>

      <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="4000" top right elevation="24">
        <div class="snackbar-copy">
          <v-icon color="white">{{ snackbar.icon }}</v-icon>
          <span>{{ snackbar.text }}</span>
        </div>
        <template v-slot:action="{ attrs }">
          <v-btn icon v-bind="attrs" aria-label="Meldung schliessen" @click="snackbar.show = false"><v-icon>mdi-close</v-icon></v-btn>
        </template>
      </v-snackbar>
    </div>
  </main>
</template>

<script>
import api from "@/api"
import MarketingToolHeader from "@/views/dashboard/communication/MarketingToolHeader.vue"
import SecuredMedia from "@/views/akademie/SecuredMedia.vue"

export default {
  name: "SchulungDetail",
  components: { MarketingToolHeader, SecuredMedia },
  data() {
    return {
      session: null,
      missingModules: [],
      protocol: null,
      protocolLoading: false,
      protocolError: "",
      downloadingProtocolFileId: null,
      protocolLightboxDialog: false,
      protocolLightboxFile: null,
      registering: false,
      cancelling: false,
      loading: false,
      currentTime: Date.now(),
      clockTimer: null,
      registrationDialog: false,
      mealPreference: "none",
      mealOptions: [
        { value: "none", label: "Keine Angabe", description: "Keine Präferenz hinterlegen", icon: "mdi-minus-circle-outline" },
        { value: "meat", label: "Fleisch", description: "Fleisch ist für mich in Ordnung", icon: "mdi-silverware-fork-knife" },
        { value: "no_pork", label: "Kein Schweinefleisch", description: "Fleisch, aber ohne Schweinefleisch", icon: "mdi-cancel" },
        { value: "vegetarian", label: "Vegetarisch", description: "Ohne Fleisch und Fisch", icon: "mdi-leaf" },
        { value: "vegan", label: "Vegan", description: "Ohne tierische Produkte", icon: "mdi-sprout" },
      ],
      snackbar: { show: false, color: "success", text: "", icon: "mdi-check-circle" },
    }
  },
  computed: {
    hasMetRequirements() {
      return this.missingModules.length === 0
    },
    registrationStartTime() {
      return this.session && this.session.registrationStart ? new Date(this.session.registrationStart).getTime() : null
    },
    registrationEndTime() {
      return this.session && this.session.registrationEnd ? new Date(this.session.registrationEnd).getTime() : null
    },
    sessionStartTime() {
      return this.session && this.session.startsAt ? new Date(this.session.startsAt).getTime() : null
    },
    isSessionStarted() {
      return this.sessionStartTime !== null && this.currentTime >= this.sessionStartTime
    },
    isRegistrationOpen() {
      if (!this.session || this.session.active === false || this.session.deletedAt) return false
      if (this.registrationStartTime === null || this.registrationEndTime === null || this.sessionStartTime === null) return false
      return this.currentTime >= this.registrationStartTime && this.currentTime <= this.registrationEndTime && !this.isSessionStarted
    },
    canRegister() {
      return this.hasMetRequirements && this.isRegistrationOpen
    },
    hasCatering() {
      return !this.session || this.session.cateringMode !== "none"
    },
    hasActiveRegistration() {
      return Boolean(this.session && this.session.myRegistration && this.session.myRegistration.status !== "cancelled")
    },
    cancellationDeadlineTime() {
      if (this.sessionStartTime === null || !this.session) return null
      const rawHours = Number(this.session.cancellationDeadlineHours)
      const deadlineHours = Number.isFinite(rawHours) ? Math.max(0, rawHours) : 72
      return this.sessionStartTime - deadlineHours * 60 * 60 * 1000
    },
    canCancel() {
      if (!this.hasActiveRegistration || this.cancellationDeadlineTime === null || this.isSessionStarted) return false
      const status = this.session.myRegistration.status
      return ["registered", "waiting_list"].includes(status) && this.currentTime <= this.cancellationDeadlineTime
    },
    availableSeats() {
      if (!this.session) return 0
      return Math.max(0, this.session.maxParticipants - (this.session.registeredCount || 0))
    },
    seatUsage() {
      if (!this.session || !this.session.maxParticipants) return 0
      return Math.min(100, ((this.session.registeredCount || 0) / this.session.maxParticipants) * 100)
    },
    registrationActionLabel() {
      return this.availableSeats === 0 ? "Auf Warteliste setzen" : "Verbindlich anmelden"
    },
    registrationButtonLabel() {
      if (this.canRegister) return this.availableSeats === 0 ? "Auf Warteliste setzen" : "Jetzt anmelden"
      if (!this.session || this.session.active === false || this.session.deletedAt) return "Termin nicht verfügbar"
      if (this.isSessionStarted) return "Anmeldung geschlossen"
      if (this.registrationStartTime !== null && this.currentTime < this.registrationStartTime) return "Anmeldung noch nicht geöffnet"
      if (this.registrationEndTime !== null && this.currentTime > this.registrationEndTime) return "Anmeldung geschlossen"
      if (!this.hasMetRequirements) return "Voraussetzungen fehlen"
      return "Anmeldung geschlossen"
    },
    registrationBlockReason() {
      if (!this.session) return "Dieser Termin ist nicht verfügbar."
      if (this.session.active === false || this.session.deletedAt) return "Dieser Termin ist nicht mehr verfügbar."
      if (this.isSessionStarted) return "Dieser Termin hat bereits begonnen."
      if (this.registrationStartTime !== null && this.currentTime < this.registrationStartTime) {
        return `Die Anmeldung öffnet am ${this.formatDateTime(this.session.registrationStart)}.`
      }
      if (this.registrationEndTime !== null && this.currentTime > this.registrationEndTime) {
        return "Die Anmeldefrist ist abgelaufen."
      }
      if (!this.hasMetRequirements) return "Du musst zuerst die fehlenden Module abschliessen."
      return "Die Anmeldung ist derzeit nicht möglich."
    },
    registrationLockReason() {
      if (!this.hasActiveRegistration) return "Diese Anmeldung ist schreibgeschützt."
      const status = this.session.myRegistration.status
      if (status === "attended") return "Diese Teilnahme wurde bereits abgeschlossen."
      if (status === "no_show") return "Dieser Termin wurde als nicht besucht abgeschlossen."
      if (this.isSessionStarted) return "Der Termin hat bereits begonnen. Die Anmeldung ist schreibgeschützt."
      return "Die Stornofrist ist abgelaufen. Die Anmeldung ist schreibgeschützt."
    },
    registrationStatusText() {
      if (!this.hasActiveRegistration) return ""
      const status = this.session.myRegistration.status
      if (status === "waiting_list") return "Du stehst für diesen Termin auf der Warteliste."
      if (status === "attended") return "Deine Teilnahme wurde bestätigt."
      if (status === "no_show") return "Der Termin wurde als nicht besucht abgeschlossen."
      return "Du bist für diesen Termin eingetragen."
    },
    cancellationNote() {
      if (this.cancellationDeadlineTime === null) return "Für diesen Termin ist keine Stornofrist hinterlegt."
      if (this.currentTime <= this.cancellationDeadlineTime && !this.isSessionStarted) {
        return `Stornierung bis ${this.formatDateTime(this.cancellationDeadlineTime)} möglich.`
      }
      return "Die Stornofrist ist abgelaufen."
    },
  },
  mounted() {
    this.loadSession()
    this.clockTimer = window.setInterval(() => {
      this.currentTime = Date.now()
    }, 60000)
  },
  beforeDestroy() {
    if (this.clockTimer) window.clearInterval(this.clockTimer)
  },
  methods: {
    async loadSession() {
      this.loading = true
      this.protocol = null
      this.protocolError = ""
      try {
        this.session = await api.akademie.fetchTermin(this.$route.params.id)
        if (this.session && this.session.hasProtocol) this.loadProtocol()
        if (this.session) {
          const progressEntries = await api.akademie.fetchProgress()
          const typeModules = (this.session.trainingType && this.session.trainingType.requiredModules) || []
          const requiredModules = [...(this.session.requiredModules || []), ...typeModules].reduce((items, moduleItem) => {
            const moduleId = moduleItem && (moduleItem._id || moduleItem)
            if (moduleId && !items.some((item) => String(item._id || item) === String(moduleId))) items.push(moduleItem)
            return items
          }, [])
          this.missingModules = requiredModules.filter((requiredModule) => {
            const requiredModuleId = requiredModule && (requiredModule._id || requiredModule)
            const progress = progressEntries.find((entry) => String(entry.module && (entry.module._id || entry.module)) === String(requiredModuleId))
            return !(progress && progress.completedAt)
          })
        }
      } catch (error) {
        console.error("Fehler beim Laden des Termins:", error)
        this.notify("error", "Termin konnte nicht geladen werden", "mdi-alert-circle")
      } finally {
        this.loading = false
      }
    },
    async loadProtocol() {
      if (!this.session || !this.session._id || !this.session.hasProtocol) return
      this.protocolLoading = true
      this.protocolError = ""
      try {
        const protocol = await api.akademie.fetchTrainingProtocol(this.session._id)
        if (!protocol || protocol.status !== "completed") throw new Error("Das abgeschlossene Protokoll ist nicht verfügbar.")
        this.protocol = protocol
      } catch (error) {
        console.error("Fehler beim Laden des Schulungsprotokolls:", error)
        this.protocol = null
        this.protocolError = "Das Protokoll ist momentan nicht verfügbar."
      } finally {
        this.protocolLoading = false
      }
    },
    isProtocolImage(file) {
      return String((file && file.mimeType) || "").startsWith("image/")
    },
    isProtocolPdf(file) {
      return String((file && file.mimeType) || "") === "application/pdf"
    },
    protocolPreviewPath(file, variant) {
      if (!this.session || !file) return ""
      return api.akademie.getTrainingProtocolPreviewPath(this.session._id, file.fileId, variant)
    },
    openProtocolImage(file) {
      if (!this.isProtocolImage(file)) return
      this.protocolLightboxFile = file
      this.protocolLightboxDialog = true
    },
    async downloadProtocolFile(file) {
      if (!this.session || !file || this.downloadingProtocolFileId) return
      this.downloadingProtocolFileId = file.fileId
      try {
        await api.akademie.downloadTrainingProtocolFile(this.session._id, file.fileId, file.originalName)
      } catch (error) {
        this.notify("error", "Datei konnte nicht heruntergeladen werden.", "mdi-alert-circle")
      } finally {
        this.downloadingProtocolFileId = null
      }
    },
    formatBytes(value) {
      const bytes = Number(value)
      if (!Number.isFinite(bytes) || bytes <= 0) return "Dateigrösse unbekannt"
      if (bytes < 1024) return `${bytes} B`
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    },
    formatDate(date) {
      if (!date) return ""
      return new Date(date).toLocaleDateString("de-CH", { weekday: "long", year: "numeric", month: "long", day: "numeric" })
    },
    formatTime(date) {
      if (!date) return ""
      return new Date(date).toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" }) + " Uhr"
    },
    formatDateTime(date) {
      if (!date) return ""
      return new Date(date).toLocaleString("de-CH", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    },
    getPhase(session) {
      if (!session.registrationStart || !session.registrationEnd) return "Unbekannt"
      const startsAt = session.startsAt ? new Date(session.startsAt).getTime() : null
      const endsAt = session.endsAt ? new Date(session.endsAt).getTime() : null
      const registrationStart = new Date(session.registrationStart).getTime()
      const registrationEnd = new Date(session.registrationEnd).getTime()
      if (endsAt !== null && this.currentTime >= endsAt) return "Vergangen"
      if (startsAt !== null && this.currentTime >= startsAt) return "Läuft"
      if (this.currentTime < registrationStart) return "Geplant"
      if (this.currentTime > registrationEnd) return "Geschlossen"
      return "Geöffnet"
    },
    phaseClass(phase) {
      if (phase === "Geöffnet") return "phase-open"
      if (phase === "Geplant" || phase === "Läuft") return "phase-planned"
      return "phase-closed"
    },
    statusLabel(status) {
      const labels = {
        registered: "Angemeldet",
        waiting_list: "Auf Warteliste",
        attended: "Teilgenommen",
        no_show: "Nicht teilgenommen",
      }
      return labels[status] || status
    },
    registrationStateClass(status) {
      if (status === "waiting_list") return "is-waiting"
      if (status === "no_show") return "is-no-show"
      return "is-confirmed"
    },
    registrationStatusIcon(status) {
      if (status === "waiting_list") return "mdi-clock-outline"
      if (status === "no_show") return "mdi-account-off-outline"
      if (status === "attended") return "mdi-check-decagram-outline"
      return "mdi-check"
    },
    mealPreferenceLabel(value) {
      const option = this.mealOptions.find((item) => item.value === value)
      return option ? option.label : "Keine Angabe"
    },
    notify(color, text, icon = "mdi-information") {
      this.snackbar = { show: true, color, text, icon }
    },
    openRegistrationDialog() {
      if (!this.canRegister || !this.session) return
      if (this.session.myRegistration && this.session.myRegistration.status !== "cancelled") {
        this.notify("warning", "Du bist bereits angemeldet.", "mdi-information")
        return
      }
      this.mealPreference = this.hasCatering ? (this.session.myRegistration && this.session.myRegistration.mealPreference) || "none" : "none"
      this.registrationDialog = true
    },
    async register() {
      if (!this.canRegister || !this.session) return
      if (this.session.myRegistration && this.session.myRegistration.status !== "cancelled") {
        this.notify("warning", "Du bist bereits angemeldet.", "mdi-information")
        return
      }
      this.registering = true
      try {
        const mealPreference = this.hasCatering ? this.mealPreference : "none"
        const registration = await api.akademie.registerForTermin(this.session._id, { mealPreference })
        this.$set(this.session, "myRegistration", {
          _id: registration._id,
          status: registration.status,
          mealPreference: registration.mealPreference || this.mealPreference,
        })
        this.registrationDialog = false
        this.$set(this.session, "registeredCount", (this.session.registeredCount || 0) + (registration.status === "registered" ? 1 : 0))
        if (registration.status === "waiting_list") {
          this.notify("warning", "Plätze ausgebucht – du stehst auf der Warteliste.", "mdi-clock-outline")
        } else {
          this.notify("success", "Anmeldung erfolgreich!", "mdi-check-circle")
        }
      } catch (error) {
        const message = (error.response && error.response.data && error.response.data.error) || "Anmeldung fehlgeschlagen"
        if (error.response && error.response.status === 409 && error.response.data && error.response.data.registration) {
          this.$set(this.session, "myRegistration", {
            _id: error.response.data.registration._id,
            status: error.response.data.registration.status,
            mealPreference: error.response.data.registration.mealPreference || "none",
          })
          this.registrationDialog = false
        }
        this.notify("error", message, "mdi-alert-circle")
      } finally {
        this.registering = false
      }
    },
    async cancelRegistration() {
      if (!this.session || !this.session.myRegistration) return
      if (!this.canCancel) {
        this.notify("warning", this.registrationLockReason, "mdi-lock-outline")
        return
      }
      if (!confirm("Anmeldung wirklich stornieren?")) return
      this.cancelling = true
      try {
        await api.akademie.cancelRegistration(this.session.myRegistration._id)
        this.$set(this.session, "myRegistration", null)
        this.$set(this.session, "hasProtocol", false)
        this.protocol = null
        this.protocolLightboxDialog = false
        this.protocolLightboxFile = null
        if (this.session.registeredCount > 0) this.session.registeredCount -= 1
        this.notify("success", "Anmeldung storniert.", "mdi-check-circle")
      } catch (error) {
        const message = (error.response && error.response.data && error.response.data.error) || "Stornierung fehlgeschlagen."
        this.notify("error", message, "mdi-alert-circle")
      } finally {
        this.cancelling = false
      }
    },
  },
}
</script>

<style scoped>
.training-detail-page {
  --surface: rgba(255, 255, 255, 0.035);
  --surface-strong: rgba(255, 255, 255, 0.055);
  --border: rgba(255, 255, 255, 0.1);
  --border-strong: rgba(255, 255, 255, 0.16);
  --text: #f4f4f5;
  --muted: #a3a3a8;
  min-height: 100vh;
  min-height: 100dvh;
  padding: 1.5rem;
  color: var(--text);
  background: radial-gradient(circle at 15% -5%, rgba(0, 150, 64, 0.13), transparent 28rem), #121212;
  font-size: 1rem;
}

.training-shell {
  width: 100%;
  max-width: 78rem;
  margin: 0 auto;
}

.icon-action {
  width: 2.75rem !important;
  min-width: 2.75rem !important;
  height: 2.75rem !important;
  border: 1px solid var(--border);
  border-radius: 0.8125rem !important;
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.055) !important;
}

.phase-badge,
.refresh-badge {
  display: inline-flex;
  min-height: 2.75rem;
  align-items: center;
  gap: 0.4375rem;
  padding: 0.625rem 0.875rem;
  border-radius: 0.8125rem;
  font-size: 0.875rem;
  font-weight: 750;
}

.phase-open {
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.15);
}

.phase-planned {
  color: #fcd34d;
  background: rgba(245, 158, 11, 0.15);
}

.phase-closed,
.refresh-badge {
  color: #d4d4d8;
  background: rgba(255, 255, 255, 0.075);
}

.refresh-badge .v-icon {
  color: currentColor;
}

.training-image {
  position: relative;
  min-height: 15rem;
  margin-top: 1rem;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 1.375rem;
  background-position: center;
  background-size: cover;
}

.training-image-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(18, 18, 18, 0.72), transparent 65%);
}

.detail-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.65fr) minmax(19rem, 0.75fr);
  align-items: start;
  gap: 1rem;
  margin-top: 1rem;
}

.training-type-banner {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  padding: 0.875rem;
  border: 1px solid rgba(88, 211, 136, 0.2);
  border-radius: 1rem;
  background: rgba(0, 150, 64, 0.08);
}

.training-type-banner > span {
  display: grid;
  width: 2.625rem;
  height: 2.625rem;
  flex: 0 0 auto;
  place-items: center;
  border-radius: 0.8125rem;
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.14);
}

.training-type-banner > span .v-icon {
  color: currentColor;
}

.training-type-banner > div {
  display: grid;
  min-width: 0;
  gap: 0.125rem;
}

.training-type-banner small {
  color: #72d99b;
  font-size: 0.75rem;
  font-weight: 800;
  text-transform: capitalize;
}

.training-type-banner strong {
  color: #fff;
  font-size: 0.9375rem;
  line-height: 1.4;
}

.training-detail-page ::v-deep .training-header .header-copy p,
.training-type-banner p {
  white-space: break-spaces;
  overflow-wrap: anywhere;
}

.training-type-banner p {
  margin: 0.125rem 0 0;
  color: var(--muted);
  font-size: 0.875rem;
  line-height: 1.45;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.info-card {
  display: flex;
  min-height: 7.5rem;
  align-items: center;
  gap: 0.875rem;
  padding: 1rem;
  border: 1px solid var(--border);
  border-radius: 1.125rem !important;
  background: var(--surface) !important;
}

.info-icon,
.surface-icon,
.booking-icon {
  display: grid;
  flex: 0 0 auto;
  place-items: center;
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
}

.info-icon {
  width: 2.875rem;
  height: 2.875rem;
  border-radius: 0.875rem;
}

.info-icon.icon-blue {
  color: #7dd3fc;
  background: rgba(14, 165, 233, 0.11);
}

.info-icon .v-icon,
.surface-icon .v-icon,
.booking-icon .v-icon {
  color: currentColor;
}

.info-card > div {
  display: grid;
  min-width: 0;
}

.info-card small {
  color: var(--muted);
  font-size: 0.75rem;
  font-weight: 700;
}

.info-card strong {
  margin-top: 0.1875rem;
  color: #ffffff;
  font-size: 0.9375rem;
  line-height: 1.4;
}

.info-card div > span {
  margin-top: 0.1875rem;
  color: #b8b8bc;
  font-size: 0.75rem;
}

.content-surface,
.booking-card {
  border: 1px solid var(--border);
  border-radius: 1.375rem !important;
  background: rgba(255, 255, 255, 0.03) !important;
  box-shadow: 0 1rem 2.75rem rgba(0, 0, 0, 0.12);
}

.content-surface {
  margin-top: 1rem;
  padding: 1.375rem;
}

.surface-header,
.booking-heading,
.seat-overview,
.registration-state,
.snackbar-copy {
  display: flex;
  align-items: center;
}

.surface-header {
  gap: 0.75rem;
}

.surface-icon,
.booking-icon {
  width: 2.625rem;
  height: 2.625rem;
  border-radius: 0.8125rem;
}

.surface-eyebrow {
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.surface-header h2,
.booking-heading h2 {
  margin: 0.1875rem 0 0;
  color: var(--text);
  font-size: 1.25rem;
  line-height: 1.3;
}

.surface-header p {
  margin: 0.25rem 0 0;
  color: var(--muted);
  font-size: 0.875rem;
  line-height: 1.5;
}

.requirement-state {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-top: 1rem;
  padding: 0.875rem;
  border: 1px solid var(--border);
  border-radius: 0.875rem;
}

.requirement-state > span {
  display: grid;
  width: 2.25rem;
  height: 2.25rem;
  flex: 0 0 auto;
  place-items: center;
  border-radius: 0.6875rem;
}

.requirement-state > span .v-icon {
  color: currentColor;
  font-size: 1.25rem;
}

.has-missing {
  color: #ff9aa4;
  background: rgba(244, 63, 94, 0.075);
}

.has-missing > span {
  background: rgba(244, 63, 94, 0.13);
}

.is-ready {
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.075);
}

.is-ready > span {
  background: rgba(0, 150, 64, 0.13);
}

.requirement-state strong {
  color: #ffffff;
  font-size: 0.9375rem;
}

.requirement-state p,
.requirement-state ul {
  margin: 0.25rem 0 0;
  color: #c7c7cb;
  font-size: 0.875rem;
  line-height: 1.5;
}

.requirement-state ul {
  padding-left: 1.25rem;
}

.protocol-icon {
  color: #7dd3fc;
  background: rgba(14, 165, 233, 0.12);
}

.protocol-loading {
  display: flex;
  min-height: 8rem;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  color: #b8b8bc;
  font-size: 0.875rem;
}

.protocol-summary {
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  margin-top: 1rem;
  padding: 0.875rem;
  border: 1px solid rgba(125, 211, 252, 0.16);
  border-radius: 0.875rem;
  color: #d4d4d8;
  background: rgba(14, 165, 233, 0.065);
}

.protocol-intro {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  margin-top: 1rem;
}

.protocol-intro > span {
  display: grid;
  width: 2.5rem;
  height: 2.5rem;
  flex: 0 0 auto;
  place-items: center;
  border-radius: 0.75rem;
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.13);
}

.protocol-intro > span .v-icon {
  color: currentColor;
  font-size: 1.25rem;
}

.protocol-intro > div {
  display: grid;
  min-width: 0;
  gap: 0.125rem;
}

.protocol-intro strong {
  color: #ffffff;
  font-size: 0.9375rem;
  line-height: 1.4;
}

.protocol-intro small {
  color: #a3a3a8;
  font-size: 0.75rem;
}

.protocol-summary .v-icon {
  flex: 0 0 auto;
  color: #7dd3fc;
  font-size: 1.25rem;
}

.protocol-summary p {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.6;
  white-space: pre-line;
}

.protocol-files-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
  margin-top: 1rem;
}

.protocol-file-card {
  display: flex;
  min-width: 0;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 1rem;
  background: rgba(0, 0, 0, 0.14);
}

.protocol-file-preview {
  position: relative;
  display: block;
  width: 100%;
  height: 11rem;
  padding: 0;
  overflow: hidden;
  border: 0;
  border-bottom: 1px solid var(--border);
  outline: none;
  background: rgba(0, 0, 0, 0.28);
  cursor: pointer;
}

.protocol-file-preview:focus-visible {
  box-shadow: inset 0 0 0 3px rgba(88, 211, 136, 0.42);
}

.protocol-file-preview ::v-deep .secured-media-wrapper,
.protocol-file-preview ::v-deep .secured-media-content.image {
  width: 100%;
  height: 100%;
  min-height: 100%;
}

.protocol-file-preview ::v-deep .secured-media-content.image {
  object-fit: cover;
}

.preview-hint {
  position: absolute;
  right: 0.625rem;
  bottom: 0.625rem;
  display: inline-flex;
  min-height: 2rem;
  align-items: center;
  gap: 0.3125rem;
  padding: 0.375rem 0.625rem;
  border-radius: 999px;
  color: #ffffff;
  background: rgba(0, 0, 0, 0.72);
  font-size: 0.75rem;
  font-weight: 750;
}

.preview-hint .v-icon {
  color: currentColor;
  font-size: 1rem;
}

.protocol-file-placeholder {
  display: flex;
  height: 11rem;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 0.375rem;
  border-bottom: 1px solid var(--border);
  color: #ff9aa4;
  background: linear-gradient(135deg, rgba(244, 63, 94, 0.11), rgba(255, 255, 255, 0.02));
}

.protocol-file-placeholder .v-icon {
  color: currentColor;
  font-size: 3rem;
}

.protocol-file-placeholder span {
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.08em;
}

.protocol-file-copy {
  display: grid;
  min-width: 0;
  gap: 0.1875rem;
  padding: 0.875rem 0.875rem 0;
}

.protocol-file-copy strong {
  overflow: hidden;
  color: #f4f4f5;
  font-size: 0.875rem;
  line-height: 1.4;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.protocol-file-copy span {
  color: #8f9095;
  font-size: 0.75rem;
}

.protocol-file-copy p {
  margin: 0.375rem 0 0;
  color: #b8b8bc;
  font-size: 0.875rem;
  line-height: 1.5;
}

.protocol-file-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.375rem;
  margin-top: auto;
  padding: 0.875rem;
}

.protocol-view-action,
.protocol-download-action,
.protocol-retry-action {
  min-height: 2.75rem !important;
  border-radius: 0.75rem !important;
  font-size: 0.75rem !important;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.protocol-view-action {
  color: #d4d4d8 !important;
}

.protocol-error {
  display: grid;
  grid-template-columns: 2.5rem minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1rem;
  padding: 0.875rem;
  border: 1px solid rgba(244, 63, 94, 0.18);
  border-radius: 0.875rem;
  background: rgba(244, 63, 94, 0.065);
}

.protocol-error > span {
  display: grid;
  width: 2.5rem;
  height: 2.5rem;
  place-items: center;
  border-radius: 0.75rem;
  color: #ff9aa4;
  background: rgba(244, 63, 94, 0.12);
}

.protocol-error > span .v-icon {
  color: currentColor;
}

.protocol-error strong {
  color: #ffffff;
  font-size: 0.875rem;
}

.protocol-error p {
  margin: 0.1875rem 0 0;
  color: #c7c7cb;
  font-size: 0.875rem;
}

.protocol-lightbox-card {
  overflow: hidden;
  border: 1px solid var(--border) !important;
  border-radius: 1.125rem !important;
  background: #151716 !important;
}

.protocol-lightbox-header,
.protocol-lightbox-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
}

.protocol-lightbox-header {
  border-bottom: 1px solid var(--border);
}

.protocol-lightbox-header > div {
  display: grid;
  min-width: 0;
  gap: 0.1875rem;
}

.protocol-lightbox-header strong {
  overflow: hidden;
  color: #ffffff;
  font-size: 1rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.protocol-lightbox-header span,
.protocol-lightbox-footer > span {
  color: #a3a3a8;
  font-size: 0.875rem;
}

.protocol-lightbox-media {
  display: grid;
  min-height: 20rem;
  max-height: 74vh;
  place-items: center;
  padding: 0.75rem;
  overflow: auto;
  background: #090a09;
}

.protocol-lightbox-media ::v-deep .secured-media-content.image {
  max-height: 70vh;
}

.protocol-lightbox-footer {
  border-top: 1px solid var(--border);
}

.booking-card {
  position: sticky;
  top: 1rem;
  padding: 1.25rem;
}

.booking-heading,
.seat-overview {
  justify-content: space-between;
  gap: 0.75rem;
}

.seat-overview {
  margin: 1.25rem 0;
  padding: 1rem 0;
  border-top: 1px solid rgba(255, 255, 255, 0.075);
  border-bottom: 1px solid rgba(255, 255, 255, 0.075);
}

.seat-overview > div {
  display: grid;
}

.seat-overview small {
  color: var(--muted);
  font-size: 0.75rem;
}

.seat-overview strong {
  color: #ffffff;
  font-size: 2rem;
  line-height: 1.15;
}

.seat-overview strong span {
  color: #a3a3a8;
  font-size: 1rem;
}

.no-catering-notice {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin: 0 0 0.875rem;
  padding: 0.875rem;
  border: 1px solid rgba(245, 158, 11, 0.24);
  border-radius: 0.875rem;
  color: #fcd34d;
  background: rgba(245, 158, 11, 0.075);
}

.no-catering-notice > span {
  display: grid;
  width: 2.25rem;
  height: 2.25rem;
  flex: 0 0 2.25rem;
  place-items: center;
  border-radius: 0.6875rem;
  background: rgba(255, 255, 255, 0.07);
}

.no-catering-notice .v-icon {
  color: currentColor;
  font-size: 1.25rem;
}

.no-catering-notice strong {
  display: block;
  color: #ffffff;
  font-size: 0.9375rem;
  line-height: 1.4;
}

.no-catering-notice p {
  margin: 0.1875rem 0 0;
  color: #e4e4e7;
  font-size: 0.875rem;
  line-height: 1.5;
}

.registration-state {
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  padding: 0.875rem;
  border-radius: 0.875rem;
}

.registration-state > span {
  display: grid;
  width: 2.25rem;
  height: 2.25rem;
  flex: 0 0 auto;
  place-items: center;
  border-radius: 0.6875rem;
  background: rgba(255, 255, 255, 0.08);
}

.registration-state > span .v-icon {
  color: currentColor;
  font-size: 1.25rem;
}

.registration-state strong {
  color: #ffffff;
  font-size: 0.9375rem;
}

.registration-state p {
  margin: 0.1875rem 0 0;
  color: currentColor;
  font-size: 0.875rem;
  opacity: 0.9;
}

.registration-state .registered-meal {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid currentColor;
  font-size: 0.75rem;
}

.registered-meal .v-icon {
  color: currentColor;
  font-size: 1rem;
}

.is-confirmed {
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.13);
}

.is-waiting {
  color: #fcd34d;
  background: rgba(245, 158, 11, 0.13);
}

.is-no-show {
  color: #ff9aa4;
  background: rgba(244, 63, 94, 0.13);
}

.register-action,
.cancel-action,
.back-action {
  min-height: 3rem !important;
  border-radius: 0.8125rem !important;
  font-size: 0.875rem !important;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.register-action {
  color: #ffffff !important;
}

.registration-locked-note {
  display: flex;
  min-height: 3rem;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.8125rem;
  color: #b8b8bc;
  background: rgba(255, 255, 255, 0.045);
  font-size: 0.875rem;
  line-height: 1.45;
}

.registration-locked-note .v-icon {
  flex: 0 0 auto;
  color: #8f9095;
  font-size: 1.125rem;
}

.cancellation-note {
  display: flex;
  align-items: flex-start;
  gap: 0.4375rem;
  margin: 0.875rem 0 0;
  color: var(--muted);
  font-size: 0.75rem;
  line-height: 1.5;
}

.cancellation-note .v-icon {
  color: #8f9095;
  font-size: 1rem;
}

.loading-surface {
  display: flex;
  min-height: 70vh;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  color: var(--muted);
  text-align: center;
}

.loading-surface > span {
  display: grid;
  width: 3.5rem;
  height: 3.5rem;
  margin-bottom: 0.875rem;
  place-items: center;
  border-radius: 1rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
}

.loading-surface > span .v-icon {
  color: currentColor;
}

.loading-surface h2 {
  margin: 0;
  color: var(--text);
  font-size: 1.25rem;
}

.loading-surface p {
  margin: 0.375rem 0 0;
  font-size: 0.875rem;
}

.back-action {
  margin-top: 1rem;
}

.meal-dialog-card {
  padding: 1.25rem;
  border: 1px solid rgba(88, 211, 136, 0.22) !important;
  border-radius: 1.25rem !important;
  background: radial-gradient(circle at 0 0, rgba(0, 150, 64, 0.12), transparent 18rem), #1a1c1b !important;
  box-shadow: 0 1.5rem 4rem rgba(0, 0, 0, 0.38) !important;
}

.meal-dialog-header {
  display: grid;
  grid-template-columns: 3rem minmax(0, 1fr) 2.75rem;
  align-items: center;
  gap: 0.75rem;
}

.meal-dialog-icon {
  display: grid;
  width: 3rem;
  height: 3rem;
  place-items: center;
  border-radius: 0.875rem;
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.16);
}

.meal-dialog-icon .v-icon {
  color: currentColor;
  font-size: 1.5rem;
}

.meal-dialog-header small {
  color: #7ee5a6;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.meal-dialog-header h2 {
  margin: 0.125rem 0 0;
  color: #ffffff;
  font-size: 1.375rem;
  line-height: 1.25;
}

.meal-dialog-close {
  width: 2.75rem !important;
  height: 2.75rem !important;
  color: #d4d4d8 !important;
  background: rgba(255, 255, 255, 0.055) !important;
}

.meal-dialog-intro {
  margin: 1rem 0;
  color: #c7c7cb;
  font-size: 1rem;
  line-height: 1.55;
}

.no-catering-dialog-note {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin: 1rem 0 0;
  padding: 1rem;
  border: 1px solid rgba(245, 158, 11, 0.24);
  border-radius: 0.875rem;
  color: #fcd34d;
  background: rgba(245, 158, 11, 0.075);
}

.no-catering-dialog-note .v-icon {
  flex: 0 0 auto;
  color: currentColor;
  font-size: 1.25rem;
}

.no-catering-dialog-note p {
  margin: 0;
  color: #f4f4f5;
  font-size: 1rem;
  line-height: 1.55;
}

.meal-options {
  display: grid;
  gap: 0.5rem;
}

.meal-option {
  display: grid;
  width: 100%;
  min-height: 4.5rem;
  grid-template-columns: 2.5rem minmax(0, 1fr) 1.5rem;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.875rem;
  color: #d4d4d8;
  background: rgba(255, 255, 255, 0.035);
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: border-color 160ms ease, background-color 160ms ease;
}

.meal-option:hover,
.meal-option:focus-visible {
  border-color: rgba(88, 211, 136, 0.34);
  outline: none;
  background: rgba(0, 150, 64, 0.075);
}

.meal-option.is-selected {
  border-color: rgba(88, 211, 136, 0.46);
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.12);
}

.meal-option-icon {
  display: grid;
  width: 2.5rem;
  height: 2.5rem;
  place-items: center;
  border-radius: 0.75rem;
  color: currentColor;
  background: rgba(255, 255, 255, 0.06);
}

.meal-option-icon .v-icon,
.meal-option-check {
  color: currentColor;
  font-size: 1.25rem;
}

.meal-option-copy {
  display: grid;
  min-width: 0;
}

.meal-option-copy strong {
  color: #f4f4f5;
  font-size: 0.9375rem;
  line-height: 1.35;
}

.meal-option-copy small {
  margin-top: 0.125rem;
  color: #a3a3a8;
  font-size: 0.875rem;
  line-height: 1.35;
}

.meal-privacy-note {
  display: flex;
  align-items: flex-start;
  gap: 0.4375rem;
  margin: 0.875rem 0 0;
  color: #a3a3a8;
  font-size: 0.875rem;
  line-height: 1.5;
}

.meal-privacy-note .v-icon {
  color: #8f9095;
  font-size: 1.125rem;
}

.meal-dialog-actions {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 0.5rem;
  margin-top: 1rem;
}

.meal-cancel-action,
.meal-confirm-action {
  min-height: 3rem !important;
  border-radius: 0.75rem !important;
  font-size: 0.875rem !important;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.meal-cancel-action {
  color: #c7c7cb !important;
}

.meal-confirm-action {
  color: #ffffff !important;
}

.snackbar-copy {
  gap: 0.5rem;
}

@media (max-width: 78.9375rem) {
  .training-detail-page {
    padding: 1.125rem;
  }
}

@media (max-width: 59.9375rem) {
  .detail-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .booking-card {
    position: static;
  }
}

@media (max-width: 37.4375rem) {
  .training-detail-page {
    padding: 0.75rem;
  }

  .training-detail-page ::v-deep .training-header.marketing-tool-header--mobile-compact .header-content {
    min-width: 0;
    flex: 1 1 auto;
  }

  .training-detail-page ::v-deep .training-header.marketing-tool-header--mobile-compact .header-copy {
    min-width: 0;
  }

  .training-header .phase-badge,
  .training-header .refresh-badge {
    min-height: 2.25rem;
    padding: 0.4375rem 0.625rem;
    border-radius: 0.6875rem;
    font-size: 0.75rem;
    white-space: nowrap;
  }

  .training-image {
    min-height: 11rem;
    margin-top: 0.75rem;
    border-radius: 1.0625rem;
  }

  .detail-grid {
    margin-top: 0.75rem;
  }

  .info-grid {
    grid-template-columns: minmax(0, 1fr);
    gap: 0.5rem;
  }

  .info-card {
    min-height: 6.5rem;
  }

  .content-surface,
  .booking-card {
    padding: 0.875rem;
    border-radius: 1.0625rem !important;
  }

  .protocol-files-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .protocol-file-preview,
  .protocol-file-placeholder {
    height: 12rem;
  }

  .protocol-file-actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .protocol-download-action:only-child {
    grid-column: 1 / -1;
  }

  .protocol-error {
    grid-template-columns: 2.5rem minmax(0, 1fr);
  }

  .protocol-retry-action {
    grid-column: 1 / -1;
  }

  .protocol-lightbox-footer {
    align-items: stretch;
    flex-direction: column;
  }

  .protocol-lightbox-media {
    min-height: 14rem;
    padding: 0.5rem;
  }

  .meal-dialog-card {
    padding: 0.875rem;
    border-radius: 1rem !important;
  }

  .meal-dialog-actions {
    grid-template-columns: minmax(0, 1fr);
  }

  .meal-confirm-action {
    grid-row: 1;
  }
}
</style>
